import { Injectable, inject } from '@angular/core';

import { CalendarService } from '../calendar/calendar.service';

export interface GanttResourceRequest {
  type: string;
  resourceCategory: string;
  resourceTemplateId?: unknown;
  fields?: unknown;
  resourceId?: unknown;
  eventId?: unknown;
  [key: string]: unknown;
}

export interface GanttEventInput {
  id: unknown;
  name: string;
  templateId?: unknown;
  startTime: string | Date;
  endTime: string | Date;
  resources?: GanttResourceRequest[];
  resourceDetails?: Array<{ id: unknown; name: string }>;
  [key: string]: unknown;
}

export interface GanttOwnerInput {
  eventId: unknown;
  eventName: string;
  eventTemplateId?: unknown;
  startDateTime: string | Date;
  endDateTime: string | Date;
}

@Injectable({ providedIn: 'root' })
export class GanttTransformService {
  private readonly calendarService = inject(CalendarService);

  gId = 1;

  init(): boolean {
    return true;
  }

  ganttId(): number {
    return this.gId++;
  }

  event(event: GanttEventInput, transformResources?: boolean) {
    const transResources = typeof transformResources === 'undefined' ? true : transformResources;
    const resources: ReturnType<GanttTransformService['resource']>[] = [];

    const newEvent: Record<string, unknown> = {};
    newEvent.id = this.ganttId();
    newEvent.text = event.name;
    newEvent.eventId = event.id;
    newEvent.start_date = this.calendarService.parseDate(event.startTime);

    const diff = Math.abs((newEvent.start_date as Date).getTime() - new Date(event.endTime).getTime());
    newEvent.duration = Math.floor(diff / 1000 / 60 / 15);

    newEvent.open = false;
    newEvent.progress = 1;
    newEvent.internal_id = event.id;
    newEvent.template_id = event.templateId;
    newEvent.is_event = true;
    newEvent.is_resource_add = false;

    const resourceIds: Record<string, unknown> = {};
    newEvent.resourceIds = resourceIds;

    if (transResources && Object.prototype.hasOwnProperty.call(event, 'resources') && event.resources) {
      for (let i = 0; i < event.resources.length; i++) {
        const currentResource = event.resources[i];
        currentResource.eventId = event.id;
        const resource = this.resource(currentResource, newEvent, event.resourceDetails ?? []);
        resources.push(resource);
        if (resource.internal_id !== null) {
          resourceIds[String(resource.internal_id)] = resource.id;
        }
      }
    }

    return { event: newEvent, resources };
  }

  resource(
    currentResource: GanttResourceRequest,
    newEvent: Record<string, unknown>,
    resourceDetails: Array<{ id: unknown; name: string }>,
  ) {
    const newResource: Record<string, unknown> = {};

    newResource.id = this.ganttId();
    newResource.text = currentResource.type;

    newResource.start_date = newEvent.start_date;
    newResource.duration = newEvent.duration;
    newResource.parent = newEvent.id;
    newResource.r_category = currentResource.resourceCategory;
    newResource.r_type = currentResource.type;
    newResource.request = currentResource;

    newResource.progress = 1;
    newResource.is_event = false;
    newResource.is_resource_add = false;
    newResource.fulfilled = false;

    newResource.internal_id = null;

    newResource.available_query = {
      resourceCategory: currentResource.resourceCategory,
      type: currentResource.type,
      resourceTemplateId: currentResource.resourceTemplateId,
      fields: currentResource.fields,
      quantity: 1,
    };

    if (Object.prototype.hasOwnProperty.call(currentResource, 'resourceId') && resourceDetails.length > 0) {
      newResource.internal_id = currentResource.resourceId;
      newResource.fulfilled = true;

      let details: { id: unknown; name: string } | null = null;
      for (const key in resourceDetails) {
        if (Object.prototype.hasOwnProperty.call(resourceDetails, key)) {
          if (resourceDetails[key].id == currentResource.resourceId) {
            details = resourceDetails[key];
          }
        }
      }

      if (details) {
        newResource.text = details.name + ', ' + currentResource.type;
        newResource.name = details.name;
      }
    }

    return newResource as { id: number; internal_id: unknown; [key: string]: unknown };
  }

  owner(event: GanttOwnerInput) {
    const newEvent: Record<string, unknown> = {};
    newEvent.id = this.ganttId();
    newEvent.text = event.eventName;
    newEvent.start_date = new Date(event.startDateTime);

    const diff = Math.abs((newEvent.start_date as Date).getTime() - new Date(event.endDateTime).getTime());
    newEvent.duration = Math.floor(diff / 1000 / 60 / 15);

    newEvent.open = false;
    newEvent.progress = 1;
    newEvent.internal_id = event.eventId;
    newEvent.template_id = event.eventTemplateId;
    newEvent.is_event = true;
    newEvent.is_resource_add = false;

    return newEvent;
  }
}
